import {testIdProps} from '@github-ui/test-id-props'
import {BlockedIcon} from '@primer/octicons-react'
import {Token} from '@primer/react'
import {Suspense} from 'react'
import {type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {IssueItemBlockedBy$key} from './__generated__/IssueItemBlockedBy.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import styles from './IssueItem.module.css'
import {IssuesIndexSecondaryGraphqlQuery} from './IssueRow'

type Props = {
  metadataRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
  issueId: string
  href?: string
}

const issueBlockedByFragment = graphql`
  fragment IssueItemBlockedBy on Issue {
    state
    issueDependenciesSummary {
      blockedBy
    }
  }
`

export function IssueItemBlockedBy({metadataRef, issueId, href}: Props) {
  if (!metadataRef) return null

  return (
    <Suspense fallback={null}>
      <IssueItemBlockedByFetched issueId={issueId} metadataRef={metadataRef} href={href} />
    </Suspense>
  )
}

type FetchedProps = {
  metadataRef: PreloadedQuery<IssueRowSecondaryQuery>
  issueId: string
  href?: string
}

export function IssueItemBlockedByFetched({metadataRef, issueId, href}: FetchedProps) {
  const {nodes} = usePreloadedQuery<IssueRowSecondaryQuery>(IssuesIndexSecondaryGraphqlQuery, metadataRef)
  const issueNode = nodes?.find(node => node?.id === issueId)

  if (!issueNode) return null

  return <IssueItemBlockedByInternal blockedByKey={issueNode} href={href} />
}

export function IssueItemBlockedByInternal({
  blockedByKey,
  href,
}: {
  blockedByKey?: IssueItemBlockedBy$key
  href?: string
}) {
  const data = useFragment(issueBlockedByFragment, blockedByKey)

  if (data?.state === 'CLOSED') return null

  if (!data?.issueDependenciesSummary) return null

  const {blockedBy} = data.issueDependenciesSummary

  if (blockedBy === 0) return null

  return (
    <Token
      text="Blocked"
      leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
      className={styles.token}
      {...(href ? {as: 'a', href} : {})}
      {...testIdProps('blocked-by-token')}
    />
  )
}

try{ IssueItemBlockedBy.displayName ||= 'IssueItemBlockedBy' } catch {}
try{ IssueItemBlockedByFetched.displayName ||= 'IssueItemBlockedByFetched' } catch {}
try{ IssueItemBlockedByInternal.displayName ||= 'IssueItemBlockedByInternal' } catch {}